import React from 'react';
import { Box, Typography, Grid } from '@mui/material';
import {
  BarChart,
  Bar,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import AIOverviewCard from 'src/App/Dashboards/Market/Components/Cards/AIOverviewCard';
import { LoadingCard } from 'src/App/Dashboards/Market/Components/Cards';
import { useUpgradeMesageProFeature } from 'src/hooks/useUpgradeText';
import { COLOR_PRIMARY_2, COLOR_TEXT_2, COLOR_GREY_200, COLOR_PRIMARY_2_400 } from 'src/utils/colors';

const CHART_HEIGHT = 340;
const BAR_COLOR = '#0097B2';
const BAR_COLOR_BELOW_AVG = COLOR_PRIMARY_2_400;
const CURRENCY_SYMBOL = '$';

// Mock: by bedroom (0, 1, 2, 3, 4, 5+)
const BEDROOM_LABELS = ['0', '1', '2', '3', '4', '5+'];
const ADR_BY_BEDROOM_DATA = [
  { bedroom: '0', adr: 95 },
  { bedroom: '1', adr: 112 },
  { bedroom: '2', adr: 138 },
  { bedroom: '3', adr: 165 },
  { bedroom: '4', adr: 218 },
  { bedroom: '5+', adr: 285 },
];
const OCCUPANCY_BY_BEDROOM_DATA = [
  { bedroom: '0', occupancy: 62 },
  { bedroom: '1', occupancy: 68 },
  { bedroom: '2', occupancy: 72 },
  { bedroom: '3', occupancy: 70 },
  { bedroom: '4', occupancy: 65 },
  { bedroom: '5+', occupancy: 58 },
];
const REVENUE_BY_BEDROOM_DATA = [
  { bedroom: '0', revenue: 18500 },
  { bedroom: '1', revenue: 24800 },
  { bedroom: '2', revenue: 31200 },
  { bedroom: '3', revenue: 38500 },
  { bedroom: '4', revenue: 52000 },
  { bedroom: '5+', revenue: 72000 },
];
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const SEASONALITY_BY_BEDROOM_DATA = MONTHS_SHORT.map((month, i) => {
  const s = Math.sin((i / 12) * Math.PI * 2);
  return {
    month,
    studio: Math.round(50 + s * 20),
    '1br': Math.round(55 + s * 22),
    '2br': Math.round(60 + s * 24),
    '3br': Math.round(58 + s * 23),
    '4br': Math.round(52 + s * 20),
    '5+': Math.round(48 + s * 18),
  };
});

const axisTickStyle = {
  fontSize: 14,
  fontFamily: 'Roboto',
  fill: COLOR_TEXT_2,
  fontWeight: 500,
};

const formatCurrencyAxis = (v: number) => {
  if (v >= 1000) return `${CURRENCY_SYMBOL}${(v / 1000).toFixed(0)}k`;
  return `${CURRENCY_SYMBOL}${v}`;
};

const AdrTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const { bedroom, adr } = payload[0].payload;
  return (
    <Box sx={{ background: '#fff', p: 1.5, borderRadius: 2, boxShadow: 2 }}>
      <Typography variant="body2" fontWeight={600} color={COLOR_TEXT_2}>
        {bedroom} br: {CURRENCY_SYMBOL}{adr}
      </Typography>
    </Box>
  );
};
const OccupancyTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const { bedroom, occupancy } = payload[0].payload;
  return (
    <Box sx={{ background: '#fff', p: 1.5, borderRadius: 2, boxShadow: 2 }}>
      <Typography variant="body2" fontWeight={600} color={COLOR_TEXT_2}>
        {bedroom} br: {occupancy}%
      </Typography>
    </Box>
  );
};
const RevenueTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const { bedroom, revenue } = payload[0].payload;
  return (
    <Box sx={{ background: '#fff', p: 1.5, borderRadius: 2, boxShadow: 2 }}>
      <Typography variant="body2" fontWeight={600} color={COLOR_TEXT_2}>
        {bedroom} br: {formatCurrencyAxis(revenue)}
      </Typography>
    </Box>
  );
};

export interface PropertySizeChartsProps {
  isMobileView?: boolean;
  /** When true, use Market Intelligence layout (ADR/Occupancy/Revenue full row; Seasonality at bottom with real data when available). */
  useMarketIntelligenceLayout?: boolean;
  cachedMarketData?: any;
  /** When false (starter / not allowed), show mock data with blur overlay. */
  isUserAllowed?: boolean;
  /** Called when user clicks upgrade CTA on blur overlay (e.g. handleOnSampleClick('expert')). */
  handleOnSampleClick?: (plan: string) => void;
}

const PropertySizeCharts: React.FC<PropertySizeChartsProps> = ({
  isMobileView = false,
  useMarketIntelligenceLayout = false,
  cachedMarketData,
  isUserAllowed = true,
  handleOnSampleClick,
}) => {
  const upgradeMessage = useUpgradeMesageProFeature();
  const useThirdRow = false; // ADR / Occupancy / Revenue by bedrooms: always full row
  const adrData = React.useMemo(() => {
    if (!isUserAllowed) return ADR_BY_BEDROOM_DATA;
    const bedroom = cachedMarketData?.data?.adr_breakdown?.bedroom;
    if (bedroom && typeof bedroom === 'object') {
      return Object.entries(bedroom).map(([key, value]) => ({ bedroom: key, adr: Number(value) }));
    }
    return ADR_BY_BEDROOM_DATA;
  }, [cachedMarketData, isUserAllowed]);

  const occupancyData = React.useMemo(() => {
    if (!isUserAllowed) return OCCUPANCY_BY_BEDROOM_DATA;
    const bedroom = cachedMarketData?.data?.occupancy_breakdown?.bedroom;
    if (bedroom && typeof bedroom === 'object') {
      return Object.entries(bedroom).map(([key, value]) => ({ bedroom: key, occupancy: Number(value) }));
    }
    return OCCUPANCY_BY_BEDROOM_DATA;
  }, [cachedMarketData, isUserAllowed]);

  const revenueData = React.useMemo(() => {
    if (!isUserAllowed) return REVENUE_BY_BEDROOM_DATA;
    const bedroom = cachedMarketData?.data?.revenue_breakdown?.bedroom;
    if (bedroom && typeof bedroom === 'object') {
      return Object.entries(bedroom).map(([key, value]) => ({ bedroom: key, revenue: Number(value) }));
    }
    return REVENUE_BY_BEDROOM_DATA;
  }, [cachedMarketData, isUserAllowed]);

  const adrAverage = React.useMemo(() => {
    if (!adrData.length) return 0;
    return adrData.reduce((acc, d) => acc + d.adr, 0) / adrData.length;
  }, [adrData]);

  const occupancyAverage = React.useMemo(() => {
    if (!occupancyData.length) return 0;
    return occupancyData.reduce((acc, d) => acc + d.occupancy, 0) / occupancyData.length;
  }, [occupancyData]);

  const revenueAverage = React.useMemo(() => {
    if (!revenueData.length) return 0;
    return revenueData.reduce((acc, d) => acc + d.revenue, 0) / revenueData.length;
  }, [revenueData]);

  const seasonalityData = React.useMemo(() => {
    if (!isUserAllowed) return [];
    const bedroom = cachedMarketData?.data?.ltm_monthly_performance_breakdown?.bedroom;
    if (!bedroom || typeof bedroom !== 'object') return [];
    const sampleBedroom = bedroom['1'] ?? bedroom['0'] ?? bedroom['2'];
    if (!sampleBedroom || typeof sampleBedroom !== 'object') return [];
    const allMonths = Object.keys(sampleBedroom).sort();
    if (allMonths.length === 0) return [];
    const preferred2025 = allMonths.filter((ym) => ym >= '2025-01' && ym <= '2025-12');
    const monthsToUse =
      preferred2025.length === 12 ? preferred2025 : allMonths.slice(-12);
    const getOcc = (bdKey: string, ym: string): number | null => {
      const bd = (bedroom as Record<string, Record<string, { occupancy_rate_50?: number }>>)[bdKey];
      if (!bd || !bd[ym]) return null;
      const v = bd[ym].occupancy_rate_50;
      return typeof v === 'number' && !Number.isNaN(v) ? v : null;
    };
    return monthsToUse.map((ym) => {
      const monthNum = parseInt(ym.split('-')[1], 10);
      const monthLabel = MONTHS_SHORT[monthNum - 1] ?? ym;
      const occ5 = getOcc('5', ym);
      const occ6 = getOcc('6', ym);
      const fivePlus =
        occ5 != null && occ6 != null ? (occ5 + occ6) / 2 : (occ6 ?? occ5 ?? null);
      const round = (v: number | null) => (v != null ? Math.round(v) : 0);
      return {
        month: monthLabel,
        studio: round(getOcc('0', ym)),
        '1br': round(getOcc('1', ym)),
        '2br': round(getOcc('2', ym)),
        '3br': round(getOcc('3', ym)),
        '4br': round(getOcc('4', ym)),
        '5+': round(fivePlus),
      };
    });
  }, [cachedMarketData, isUserAllowed]);

  const adrChart = (
    <Box sx={useThirdRow ? undefined : { mt: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, color: COLOR_PRIMARY_2, fontSize: '1rem', mb: 1.5 }}>
        ADR by bedrooms
      </Typography>
      <AIOverviewCard isLoading={false} isMobileView={isMobileView}>
        <Box sx={{ width: '100%', height: CHART_HEIGHT, p: 1 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={adrData} margin={{ top: 24, right: 24, left: 0, bottom: 20 }} barCategoryGap="25%">
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLOR_GREY_200} />
              <XAxis dataKey="bedroom" axisLine={false} tickLine={false} tick={axisTickStyle} />
              <YAxis tickFormatter={formatCurrencyAxis} tickLine={false} axisLine={{ stroke: COLOR_TEXT_2, strokeDasharray: '4 4' }} tick={axisTickStyle} />
              <Tooltip content={<AdrTooltip />} cursor={false} />
              <Bar dataKey="adr" radius={[6, 6, 0, 0]} barSize={48} maxBarSize={72}>
                {adrData.map((entry, index) => (
                  <Cell key={`adr-${index}`} fill={entry.adr < adrAverage ? BAR_COLOR_BELOW_AVG : BAR_COLOR} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </AIOverviewCard>
    </Box>
  );

  const occupancyChart = (
    <Box sx={useThirdRow ? undefined : { mt: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, color: COLOR_PRIMARY_2, fontSize: '1rem', mb: 1.5 }}>
        Occupancy by bedrooms
      </Typography>
      <AIOverviewCard isLoading={false} isMobileView={isMobileView}>
        <Box sx={{ width: '100%', height: CHART_HEIGHT, p: 1 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={occupancyData} margin={{ top: 24, right: 24, left: 0, bottom: 20 }} barCategoryGap="25%">
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLOR_GREY_200} />
              <XAxis dataKey="bedroom" axisLine={false} tickLine={false} tick={axisTickStyle} />
              <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tickLine={false} axisLine={{ stroke: COLOR_TEXT_2, strokeDasharray: '4 4' }} tick={axisTickStyle} />
              <Tooltip content={<OccupancyTooltip />} cursor={false} />
              <Bar dataKey="occupancy" radius={[6, 6, 0, 0]} barSize={48} maxBarSize={72}>
                {occupancyData.map((entry, index) => (
                  <Cell key={`occ-${index}`} fill={entry.occupancy < occupancyAverage ? BAR_COLOR_BELOW_AVG : BAR_COLOR} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </AIOverviewCard>
    </Box>
  );

  const revenueChart = (
    <Box sx={useThirdRow ? undefined : { mt: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, color: COLOR_PRIMARY_2, fontSize: '1rem', mb: 1.5 }}>
        Revenue by bedrooms
      </Typography>
      <AIOverviewCard isLoading={false} isMobileView={isMobileView}>
        <Box sx={{ width: '100%', height: CHART_HEIGHT, p: 1 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={revenueData} margin={{ top: 24, right: 24, left: 0, bottom: 20 }} barCategoryGap="25%">
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLOR_GREY_200} />
              <XAxis dataKey="bedroom" axisLine={false} tickLine={false} tick={axisTickStyle} />
              <YAxis tickFormatter={formatCurrencyAxis} tickLine={false} axisLine={{ stroke: COLOR_TEXT_2, strokeDasharray: '4 4' }} tick={axisTickStyle} />
              <Tooltip content={<RevenueTooltip />} cursor={false} />
              <Bar dataKey="revenue" radius={[6, 6, 0, 0]} barSize={48} maxBarSize={72}>
                {revenueData.map((entry, index) => (
                  <Cell key={`rev-${index}`} fill={entry.revenue < revenueAverage ? BAR_COLOR_BELOW_AVG : BAR_COLOR} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </AIOverviewCard>
    </Box>
  );

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      {useThirdRow ? (
        <Grid container spacing={2} sx={{ mt: 3 }}>
          <Grid item xs={12} md={4}>
            {adrChart}
          </Grid>
          <Grid item xs={12} md={4}>
            {occupancyChart}
          </Grid>
          <Grid item xs={12} md={4}>
            {revenueChart}
          </Grid>
        </Grid>
      ) : (
        <>
          {adrChart}
          {occupancyChart}
          {revenueChart}
        </>
      )}

      {/* Seasonality by bedrooms - shown in both Overview and Market Intelligence; real data when available */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: COLOR_PRIMARY_2, fontSize: '1rem', mb: 1.5 }}>
          Seasonality by bedrooms
        </Typography>
        {useMarketIntelligenceLayout && cachedMarketData?.loading ? (
          <LoadingCard height={CHART_HEIGHT} type="bars" />
        ) : (
          <AIOverviewCard isLoading={false} isMobileView={isMobileView}>
            <Box sx={{ width: '100%', height: CHART_HEIGHT, p: 1 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={
                    useMarketIntelligenceLayout && seasonalityData.length > 0
                      ? seasonalityData
                      : SEASONALITY_BY_BEDROOM_DATA
                  }
                  margin={{ top: 24, right: 24, left: 0, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLOR_GREY_200} />
                  <XAxis dataKey="month" axisLine={false} tickLine={false} tick={axisTickStyle} />
                  <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tickLine={false} axisLine={{ stroke: COLOR_TEXT_2, strokeDasharray: '4 4' }} tick={axisTickStyle} />
                  <Tooltip
                    formatter={(value: number, name: string) => [`${Math.round(Number(value))}%`, name]}
                    labelFormatter={(label) => label}
                    contentStyle={{ fontFamily: 'Roboto' }}
                  />
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    iconType="line"
                    iconSize={10}
                    wrapperStyle={{ fontFamily: 'Roboto', fontSize: 12, fontWeight: 500, fill: COLOR_TEXT_2 }}
                    formatter={(value) => value}
                  />
                  <Line type="monotone" dataKey="studio" stroke="#89CFCB" strokeWidth={2} dot={false} name="Studio" />
                  <Line type="monotone" dataKey="1br" stroke="#75C7C3" strokeWidth={2} dot={false} name="1 br" />
                  <Line type="monotone" dataKey="2br" stroke={BAR_COLOR} strokeWidth={2} dot={false} name="2 br" />
                  <Line type="monotone" dataKey="3br" stroke="#037A90" strokeWidth={2} dot={false} name="3 br" />
                  <Line type="monotone" dataKey="4br" stroke="#1C879B" strokeWidth={2} dot={false} name="4 br" />
                  <Line type="monotone" dataKey="5+" stroke="#025A6E" strokeWidth={2} dot={false} name="5+" />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </AIOverviewCard>
        )}
      </Box>

      {/* Blur overlay when user is not allowed (starter plan) + Upgrade to Expert CTA */}
      {!isUserAllowed && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: '#fffa',
            backgroundBlendMode: 'multiply',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10,
          }}
        >
          <Typography
            variant="body1"
            onClick={() => handleOnSampleClick?.('expert')}
            sx={{
              fontSize: '20px',
              fontWeight: 500,
              color: COLOR_PRIMARY_2,
              cursor: 'pointer',
              pointerEvents: 'auto',
              textDecoration: 'underline',
            }}
          >
            {upgradeMessage}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default PropertySizeCharts;
